import React, { useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramMixedAirTemperature } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.mixedAirTemperature.v1';

type Fields = {
  outsideAirTemp: string;
  returnAirTemp: string;
  outsideAirPercentage: string;
  mixedAirTemp: string;
};
const EMPTY: Fields = {
  outsideAirTemp: '',
  returnAirTemp: '',
  outsideAirPercentage: '',
  mixedAirTemp: '',
};

/**
 * Mixed Air Temperature — MAT = (OA% × OA + (100 - OA%) × RA) / 100.
 * Solves for the missing field given any three others.
 * Temperature values converted to/from °F for math.
 */
const AirflowMixedAirTemperatureScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = useCallback((patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch })), [setFields]);

  const calculate = useCallback(() => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const oa = toImp(fields.outsideAirTemp, 'temperature');
    const ra = toImp(fields.returnAirTemp, 'temperature');
    const pct = parseFloat(fields.outsideAirPercentage);
    const mat = toImp(fields.mixedAirTemp, 'temperature');

    if (fields.mixedAirTemp === '') {
      if (pct < 0 || pct > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      setSolvedFor('mixedAirTemp');
      set({ mixedAirTemp: fromImp((pct * oa + (100 - pct) * ra) / 100, 'temperature', 1) });
    } else if (fields.outsideAirTemp === '') {
      if (pct < 0 || pct > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      if (pct === 0) {
        setError('Cannot calculate outside air temperature when OA% is 0%.');
        return;
      }
      setSolvedFor('outsideAirTemp');
      set({ outsideAirTemp: fromImp((mat * 100 - (100 - pct) * ra) / pct, 'temperature', 1) });
    } else if (fields.returnAirTemp === '') {
      if (pct < 0 || pct > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      if (pct === 100) {
        setError('Cannot calculate return air temperature when OA% is 100%.');
        return;
      }
      setSolvedFor('returnAirTemp');
      set({ returnAirTemp: fromImp((mat * 100 - pct * oa) / (100 - pct), 'temperature', 1) });
    } else if (fields.outsideAirPercentage === '') {
      if (oa === ra) {
        setError('Outside air and return air temperatures cannot be the same.');
        return;
      }
      const computed = ((mat - ra) / (oa - ra)) * 100;
      if (computed < 0 || computed > 100) {
        setError('Calculated outside air percentage is outside valid range (0-100%).');
        return;
      }
      setSolvedFor('outsideAirPercentage');
      set({ outsideAirPercentage: computed.toFixed(1) });
    }
  }, [fields, toImp, fromImp, set]);

  const clearAll = useCallback(() => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  }, [clearPersisted]);

  const result = useMemo(() => [
    fields.outsideAirTemp && { label: 'Outside Air', value: fields.outsideAirTemp, unit: unit('temperature'), emphasis: solvedFor === 'outsideAirTemp' },
    fields.returnAirTemp && { label: 'Return Air', value: fields.returnAirTemp, unit: unit('temperature'), emphasis: solvedFor === 'returnAirTemp' },
    fields.outsideAirPercentage && { label: 'OA Percentage', value: fields.outsideAirPercentage, unit: '%', emphasis: solvedFor === 'outsideAirPercentage' },
    fields.mixedAirTemp && { label: 'Mixed Air', value: fields.mixedAirTemp, unit: unit('temperature'), emphasis: solvedFor === 'mixedAirTemp' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[], [fields, unit, solvedFor]);

  return (
    <CalcShell
      title="Mixed Air Temperature"
      subtitle="Calculate mixed air temperature based on outside air, return air, and mixing percentages"
      formula={
        <>
          <FormulaLine>MAT = (OA% × OA + (100 − OA%) × RA) / 100</FormulaLine>
          <FormulaNote>
            MAT = Mixed Air Temp ({unit('temperature')}), OA% = Outside Air Percentage, OA = Outside Air Temp ({unit('temperature')}),
            RA = Return Air Temp ({unit('temperature')}).
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: MAT = (OA% × OA + (100 − OA%) × RA) / 100"
      diagram={<DiagramMixedAirTemperature />}
      outputSlots={{
        outside_air_temp_f: fields.outsideAirTemp ? toImp(fields.outsideAirTemp, 'temperature') : undefined,
        return_air_temp_f: fields.returnAirTemp ? toImp(fields.returnAirTemp, 'temperature') : undefined,
        outside_air_pct: fields.outsideAirPercentage ? parseFloat(fields.outsideAirPercentage) : undefined,
        mixed_air_temp_f: fields.mixedAirTemp ? toImp(fields.mixedAirTemp, 'temperature') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.outside_air_temp_f != null && !fields.outsideAirTemp) {
          patch.outsideAirTemp = fromImp(values.outside_air_temp_f, 'temperature', 1);
        }
        if (values.return_air_temp_f != null && !fields.returnAirTemp) {
          patch.returnAirTemp = fromImp(values.return_air_temp_f, 'temperature', 1);
        }
        if (values.outside_air_pct != null && !fields.outsideAirPercentage) {
          patch.outsideAirPercentage = values.outside_air_pct.toFixed(1);
        }
        if (values.mixed_air_temp_f != null && !fields.mixedAirTemp) {
          patch.mixedAirTemp = fromImp(values.mixed_air_temp_f, 'temperature', 1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Outside Air Temperature (${unit('temperature')})`}
          value={fields.outsideAirTemp}
          onChangeText={(v) => set({ outsideAirTemp: v })}
          rightText={unit('temperature')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Return Air Temperature (${unit('temperature')})`}
          value={fields.returnAirTemp}
          onChangeText={(v) => set({ returnAirTemp: v })}
          rightText={unit('temperature')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Outside Air Percentage (%)"
          value={fields.outsideAirPercentage}
          onChangeText={(v) => set({ outsideAirPercentage: v })}
          rightText="%"
          placeholder="0-100"
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Mixed Air Temperature (${unit('temperature')})`}
          value={fields.mixedAirTemp}
          onChangeText={(v) => set({ mixedAirTemp: v })}
          rightText={unit('temperature')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowMixedAirTemperatureScreen;
